import { useForceUpdate } from '@fluentui/react-hooks';
import * as React from 'react';
import { TransitionOutItem } from './TransitionOutItem';

function spliceEmptyItem<T>(
    enableAnimation: boolean,
    items: T[],
    exitingIndex: React.MutableRefObject<number | undefined>,
    createEmptyItem: () => T
): T[] {
    let itemsToRender = items;

    // If we removed an item, we need to splice in an empty item in its
    // place so it can animate its height going to zero.
    if (enableAnimation && exitingIndex.current !== undefined) {
        // Create a copy of the items in order to not modify the user-provided prop
        // We do not want this empty item to be provided back to us in a subsequent rerender
        itemsToRender = [...items];
        itemsToRender.splice(exitingIndex.current, 0, createEmptyItem());
    }

    return itemsToRender;
}

/**
 * This function will allow animating adding and removing items from a list. This will return a new list of items to render which will allow
 * removed items to play an exit animation before being fully removed. It will also let you know which items should have their entrance animation added
 * in order to avoid playing this animation more than once.
 * @param enableAnimation If this is false we skip both entrance and exit animations
 * @param items The items we keep track of to determine when one has been added or removed
 * @param getItemKey A way for this function to extract the key out of an item given
 * @param createEmptyItem A way for this function to create empty items to insert into the list for exit animations
 * @returns The items to render which may have an empty item inserted for an animation, a function to determine whether a given item should
 * play its entrance animation, and a function to render any empty item we added
 */
export function useAnimateListModifications<T>(
    enableAnimation: boolean,
    items: T[],
    getItemKey: (item: T) => string,
    createEmptyItem: (onRenderEmptyItem: () => JSX.Element) => T,
    itemHeight: number
): [
    itemsToRender: T[],
    shouldAnimateEntrance: (index?: number) => boolean,
    renderIfEmptyItem: (index?: number) => JSX.Element | undefined
] {
    const previousItemKeys = React.useRef<string[]>();
    const animateEntranceIndex = React.useRef<number>();
    const animateExitIndex = React.useRef<number>();

    if (enableAnimation) {
        if (
            previousItemKeys.current !== undefined &&
            items.length !== previousItemKeys.current.length
        ) {
            // We have added or removed an item from the list
            const itemKeys = items.map(getItemKey);

            // Find where in the list an item was added or removed
            const changedItemIndex = itemKeys.findIndex(
                (itemKey, index) => itemKey !== previousItemKeys.current?.[index]
            );

            if (changedItemIndex < 0) {
                // We never found the different index, which means the removed item was at the end of the list
                animateExitIndex.current = itemKeys.length;
            } else if (itemKeys.length > previousItemKeys.current.length) {
                // The index with a different key is a new item
                animateEntranceIndex.current = changedItemIndex;
            } else {
                // The index with a different key is because we removed an item
                animateExitIndex.current = changedItemIndex;
            }

            // Next iteration use the new keys as the previous value
            previousItemKeys.current = itemKeys;
        } else if (previousItemKeys.current === undefined) {
            // On first render, just keep track of the current item keys
            previousItemKeys.current = items.map(getItemKey);
        }
    }

    const shouldAnimateItemEntrance = React.useCallback(
        (index?: number) => index === animateEntranceIndex.current,
        []
    );

    const refresh = useForceUpdate();
    // When the list wants to render the fake empty item we added, use this function
    const onRenderEmptyItem = React.useCallback(() => {
        return (
            <TransitionOutItem
                height={itemHeight}
                onHeightTransitionEnd={(): void => {
                    // The empty item has transitioned to 0 height, so we should remove it from the list
                    animateExitIndex.current = undefined;
                    // Since we're modifying the user props to insert the empty item,
                    // there won't be a rerender when we want to remove it so we need to
                    // manually rerender here
                    refresh();
                }}
            />
        );
    }, [itemHeight, refresh]);

    // If we removed an item then we need to insert an empty item in its place which will run
    // the exit animation. We splice in the empty item to the user provided list of items (without modifying the original)
    const itemsToRender = spliceEmptyItem(enableAnimation, items, animateExitIndex, () =>
        // Some empty items need access to their own render function, so pass it
        createEmptyItem(onRenderEmptyItem)
    );

    const renderIfEmptyItem = React.useCallback(
        (index?: number): JSX.Element | undefined =>
            index === animateExitIndex.current ? onRenderEmptyItem() : undefined,
        [onRenderEmptyItem]
    );

    return [itemsToRender, shouldAnimateItemEntrance, renderIfEmptyItem];
}
